/**
 * DatePicker — a popover month-grid calendar matching the interview-extension
 * calendar (ExtendInterviewModal): chevron month nav, weekday header, purple
 * selected day, today outlined. Built on the shared Popover primitive.
 *
 * Value is an ISO date string ('yyyy-MM-dd' or full ISO). onChange emits
 * 'yyyy-MM-dd'. Display label is configurable via displayFormat (date-fns).
 */

import React, { useMemo, useRef, useState } from 'react';
import {
  format,
  parseISO,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  isToday,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
} from 'date-fns';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Popover } from './Popover';

export interface DatePickerProps {
  /** Selected date as an ISO string ('yyyy-MM-dd' or full ISO). */
  value?: string;
  /** Called with the selected date as 'yyyy-MM-dd'. */
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** date-fns format for the trigger label (default 'MMM yyyy'). */
  displayFormat?: string;
  className?: string;
}

function parseValue(value?: string): Date | null {
  if (!value) return null;
  try {
    const d = value.length <= 7 ? parseISO(`${value}-01`) : parseISO(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  placeholder = 'Select date',
  disabled = false,
  displayFormat = 'MMM yyyy',
  className,
}) => {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => parseValue(value), [value]);
  const [viewMonth, setViewMonth] = useState<Date>(selected ?? new Date());

  const handleToggle = () => {
    if (disabled) return;
    setViewMonth(selected ?? new Date());
    setOpen((o) => !o);
  };

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [viewMonth]);

  const weekDays = useMemo(() => {
    const ref = new Date();
    return eachDayOfInterval({
      start: startOfWeek(ref, { weekStartsOn: 1 }),
      end: endOfWeek(ref, { weekStartsOn: 1 }),
    }).map((d) => format(d, 'EEEEEE'));
  }, []);

  const handleSelect = (day: Date) => {
    onChange(format(day, 'yyyy-MM-dd'));
    setOpen(false);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={handleToggle}
        className={cn(
          'flex h-10 w-full items-center justify-between gap-2 rounded-md border bg-white px-3 text-sm transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-purple focus:border-transparent',
          disabled
            ? 'cursor-not-allowed opacity-50 border-gray-300'
            : 'border-gray-300 hover:border-purple/60',
          className,
        )}
      >
        <span className={cn('truncate font-body', selected ? 'text-dark-invirtus' : 'text-dark-invirtus/40')}>
          {selected ? format(selected, displayFormat) : placeholder}
        </span>
        <CalendarIcon className="h-4 w-4 shrink-0 text-dark-invirtus/40" aria-hidden="true" />
      </button>

      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={triggerRef}
        contentClassName="bg-white border border-gray-200 rounded-lg p-3 w-[280px]"
      >
        {/* Month nav */}
        <div className="mb-2 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setViewMonth(subMonths(viewMonth, 1))}
            className="rounded-full p-1.5 hover:bg-gray-100 transition-colors"
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4 text-dark-invirtus/70" />
          </button>
          <span className="text-sm font-semibold font-body text-dark-invirtus">
            {format(viewMonth, 'MMMM yyyy')}
          </span>
          <button
            type="button"
            onClick={() => setViewMonth(addMonths(viewMonth, 1))}
            className="rounded-full p-1.5 hover:bg-gray-100 transition-colors"
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4 text-dark-invirtus/70" />
          </button>
        </div>

        {/* Weekday header */}
        <div className="mb-1 grid grid-cols-7 gap-1">
          {weekDays.map((d, i) => (
            <div
              key={`${d}-${i}`}
              className="flex h-8 items-center justify-center text-[11px] font-semibold text-dark-invirtus/50 font-body"
            >
              {d}
            </div>
          ))}
        </div>

        {/* Day grid */}
        <div className="grid grid-cols-7 gap-1">
          {days.map((day) => {
            const isSel = !!selected && isSameDay(day, selected);
            const inMonth = isSameMonth(day, viewMonth);
            const today = isToday(day);
            return (
              <button
                key={day.toISOString()}
                type="button"
                onClick={() => handleSelect(day)}
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full text-sm font-body transition-colors',
                  isSel
                    ? 'bg-purple text-dark-invirtus font-semibold'
                    : today
                      ? 'border border-purple text-dark-invirtus'
                      : inMonth
                        ? 'text-dark-invirtus hover:bg-purple/10'
                        : 'text-gray-400 hover:bg-gray-100',
                )}
              >
                {format(day, 'd')}
              </button>
            );
          })}
        </div>
      </Popover>
    </>
  );
};

DatePicker.displayName = 'DatePicker';
